import React, { useCallback } from "react";
import {
  PatientReminderData,
  ReminderView,
} from "../../../../types/reminderTypes";
import { useUnifiedFilters } from "../../../../hooks/useUnifiedFilters";
import { usePatientSearch } from "../../../../hooks/useViewSearch";
import RemindersTable from "../../../../components/RFZOComponents/RemindersTable";
import RFZOReminderModal from "../../../../components/RFZOComponents/RFZOReminderModal";
import RFZOTherapyReminderGroupModal from "../../../../components/RFZOComponents/RFZOTherapyReminderGroupModal";
import { useNotification } from "../../../../contexts/NotificationContext";
import styles from "./PatientView.module.scss";

interface PatientViewProps {
  data: PatientReminderData[];
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  onPatientsUpdate?: (patients: PatientReminderData[]) => void;
}

const PatientView: React.FC<PatientViewProps> = ({
  data,
  loading,
  error,
  onRefresh,
  onPatientsUpdate,
}) => {
  const { showNotification } = useNotification();

  // Get search term from URL
  const { filters } = useUnifiedFilters(ReminderView.PATIENTS);

  // Apply search at component level
  const { filteredData, totalResults, hasActiveSearch } = usePatientSearch(
    data,
    filters.search || "",
    loading
  );

  // Simple optimistic updates for table operations only
  const handlePatientsChange = useCallback(
    (
      updatedPatients: PatientReminderData[],
      operationType: "note" | "therapy" | "delete" = "note"
    ) => {
      console.log("PatientView: handlePatientsChange called", {
        updatedPatientsCount: updatedPatients.length,
        operationType,
      });

      // Always update parent state immediately
      if (onPatientsUpdate) {
        onPatientsUpdate(updatedPatients);
      }

      // Only refresh when absolutely necessary
      if (operationType === "delete") {
        // Delete needs server cleanup, so refresh after short delay
        setTimeout(() => {
          console.log("PatientView: Refreshing after delete operation");
          onRefresh();
        }, 1000);
      }
      // For "note" and "therapy": NO REFRESH - trust optimistic update
      console.log(
        `PatientView: ${operationType} operation - using optimistic update, no refresh needed`
      );
    },
    [onRefresh, onPatientsUpdate]
  );

  const handleReminderSuccess = useCallback(
    (message: string, updatedPatient?: PatientReminderData) => {
      console.log("PatientView: handleReminderSuccess called", {
        message,
        hasUpdatedPatient: !!updatedPatient,
      });

      showNotification("success", message);

      // If modal provides updated patient data, use optimistic update
      if (updatedPatient && onPatientsUpdate) {
        const updatedPatients = data.map((patient) =>
          patient.id === updatedPatient.id ? updatedPatient : patient
        );
        onPatientsUpdate(updatedPatients);

        console.log("Modal edit: Using optimistic update, no refresh needed");
        return;
      }

      // Only refresh for create operations or when no updated data provided
      if (message.includes("kreiran")) {
        console.log("New reminder created, refreshing to show in list");
        onRefresh();
      } else {
        console.log("Modal edit: No refresh needed");
      }
    },
    [showNotification, onRefresh, onPatientsUpdate, data]
  );

  if (error) {
    return (
      <div className={styles.error}>
        <p>Došlo je do greške: {error}</p>
        <button onClick={onRefresh} className={styles.retryButton}>
          Pokušaj ponovo
        </button>
      </div>
    );
  }

  return (
    <div className={styles.patientView}>
      <div className={styles.controls}>
        <RFZOReminderModal onSubmit={handleReminderSuccess} />

        <RFZOTherapyReminderGroupModal />
      </div>

      {/* Show search results info */}
      {hasActiveSearch && (
        <div className={styles.searchInfo}>
          Prikazano {totalResults} od {data.length} pacijenata
        </div>
      )}

      <div className={styles.tableContainer}>
        {loading ? (
          <div className={styles.loading}>
            <div className={styles.spinner} />
            <span>Učitavanje podataka...</span>
          </div>
        ) : (
          <div>
            <RemindersTable
              patients={filteredData}
              onPatientsChange={handlePatientsChange}
              onModalSubmit={handleReminderSuccess}
              loading={loading}
              error={error}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientView;
